'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type AIPanelHeightSyncContextValue = {
  /**
   * Callback ref: attach to the element whose height the expanded AI panel
   * should match (on the Learn page, the video "feature" = header + player).
   * Passing `null` (unmount) stops observing.
   */
  registerMatchTarget: (el: HTMLElement | null) => void;
  /**
   * Measured border-box height (px) of the registered target, or `null` when
   * sync is disabled or nothing has been measured yet.
   */
  matchedHeight: number | null;
  /** Whether height syncing is active for this layout. */
  enabled: boolean;
};

const noop = () => undefined;

const AIPanelHeightSyncContext = createContext<AIPanelHeightSyncContextValue>({
  registerMatchTarget: noop,
  matchedHeight: null,
  enabled: false,
});

/**
 * Measures a target element (registered via `registerMatchTarget`) and exposes
 * its height so the expanded AI panel can be sized to match it. Decoupled on
 * purpose: the content side (LearnFeature) registers the element, the layout
 * side (WithAIPanel) consumes the height — neither imports the other.
 */
export const AIPanelHeightSyncProvider = ({
  enabled,
  children,
}: {
  enabled: boolean;
  children: React.ReactNode;
}) => {
  const [matchedHeight, setMatchedHeight] = useState<number | null>(null);
  // The registered element is held in state (not a ref) so the measurement
  // effect below re-runs when the element mounts/changes. A callback ref that
  // only stored into a ref would not retrigger the effect.
  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);

  // Stable callback ref: only captures the node. Kept free of `enabled` so its
  // identity never changes, avoiding detach/re-attach churn on the consumer.
  const registerMatchTarget = useCallback((el: HTMLElement | null) => {
    setTargetEl(el);
  }, []);

  // Measure + subscribe to size changes. Runs on the client after hydration
  // (effects never run during SSR), so `ResizeObserver`/`window` are available.
  useEffect(() => {
    if (!enabled || !targetEl) {
      setMatchedHeight(null);
      return;
    }

    let frame = 0;
    // offsetHeight is the border-box height, so the AI panel's outer edge lines
    // up with the video feature's outer edge.
    const read = () => setMatchedHeight(targetEl.offsetHeight);
    // Subsequent reads run inside rAF so they happen after layout (and to avoid
    // ResizeObserver loop warnings); the first read is synchronous so the panel
    // is sized on mount without waiting a frame.
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(read);
    };

    read();

    // Recalculate on any reflow the user can trigger:
    //  - ResizeObserver: panel drag-resize and content-driven size changes.
    //  - window resize: viewport/screen-size changes. The video's height tracks
    //    the (aspect-ratio) content width, so a width-only viewport change still
    //    changes the group's height; the window listener guarantees a
    //    re-measure even if the observer doesn't report the child-induced
    //    height change.
    const observer = new ResizeObserver(measure);
    observer.observe(targetEl);
    window.addEventListener('resize', measure);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [enabled, targetEl]);

  const value = useMemo(
    () => ({ registerMatchTarget, matchedHeight, enabled }),
    [registerMatchTarget, matchedHeight, enabled],
  );

  return (
    <AIPanelHeightSyncContext.Provider value={value}>
      {children}
    </AIPanelHeightSyncContext.Provider>
  );
};

/**
 * Access the AI-panel height-sync context: register the element to match and
 * read its measured height. Safe to call outside the provider (returns a no-op
 * default with `enabled: false`).
 */
export const useAIPanelHeightSync = (): AIPanelHeightSyncContextValue =>
  useContext(AIPanelHeightSyncContext);
